import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, CircularProgress, Tooltip, Typography } from '@mui/material';
import { GridActionsCellItem, GridColumns, GridRowModel, GridRenderCellParams, GridOverlay, GridSortModel } from '@mui/x-data-grid-pro';
import { TabContainer, TabToolbar, TabContent, TabDataGridNoRowHover } from '../../util/SharedStyles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from "@mui/icons-material/Download";
import CreateNewButton from '../buttons/CreateNewButton';
import NoRecordsMessage from '../NoRecordsMessage';
import { BlobStorageModel, BlobUploadRequestInput, BlobDocumentType, RequestResult, UserRole } from '../../gql-types.generated';
import BlobFileUploadDialog from '../dialogs/BlobFileUploadDialog';
import { getShortDateString, getTimeString } from '../../util/DateTimeUtility';
import GridCellDualVert from '../listItems/GridCellDualVert';
import { useBlobStorageDownload } from '../../hooks/useBlobStorageDownload';
import { useBlobStorageVersionUpload } from '../../hooks/useBlobStorageVersionUpload';
import { useBlobStorageVersionSetActive } from '../../hooks/useBlobStorageVersionSetActive';
import { useBlobStorageFileVersions } from '../../hooks/useBlobStorageFileVersions';

interface BlobFileVersionListProps {
    viewerRole: UserRole | undefined;
    blobDocumentId: string;
    blobDocumentType: BlobDocumentType;
    acceptedFileTypes: { [key: string]: string[] };
    fileTypeLabel: string;
    allowedExtensions: string[];
    fileVersions?: BlobStorageModel[];
}

const BlobFileVersionList: React.FC<BlobFileVersionListProps> = props => {
    const { viewerRole, blobDocumentId, blobDocumentType, acceptedFileTypes, fileTypeLabel, allowedExtensions, fileVersions: propFileVersions } = props;
    const [openModify, setOpenModify] = useState(false);
    const [fileVersionRows, setFileVersionRows] = useState<GridRowModel[]>([]);
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: 'lastModifiedTime',
            sort: 'desc',
        },
    ]);

    const { fileVersions: fetchedFileVersions, isLoading: isLoadingVersions, error, refetch } = useBlobStorageFileVersions(
        blobDocumentId,
        blobDocumentType,
        !propFileVersions
    );
    
    // Use prop data if provided, otherwise use fetched data
    const fileVersions = propFileVersions || fetchedFileVersions;
    const { downloadBlob, isDownloading } = useBlobStorageDownload();
    const { uploadVersion, uploadStatus, isUploading, clearUploadStatus } = useBlobStorageVersionUpload();
    const { setVersionActive, updateStatus, isUpdating, clearUpdateStatus } = useBlobStorageVersionSetActive();

    const onFileVersionDialogClose = useCallback(() => {
        setOpenModify(false);
    }, []);

    useEffect(() => {
        setFileVersionRows(getFileVersionRows());
    }, [fileVersions]);

    useEffect(() => {
        // Handle successful upload - refetch versions and close dialog
        if (uploadStatus?.result === RequestResult.Success) {
            clearUploadStatus();
            onFileVersionDialogClose();
            refetch();
        } else if (uploadStatus?.result === RequestResult.Fail) {
            // On failure, clear status but keep dialog open so error can be displayed
            clearUploadStatus();
        }
    }, [uploadStatus?.result, clearUploadStatus, refetch, onFileVersionDialogClose]);

    useEffect(() => {
        // Handle successful or failed set active - refetch versions
        if (updateStatus?.result === RequestResult.Success || updateStatus?.result === RequestResult.Fail) {
            clearUpdateStatus();
            refetch();
        }
    }, [updateStatus?.result, clearUpdateStatus, refetch]);

    const getSelectedRowItem = useCallback((rowId: string) => {
        if (rowId && fileVersions?.length) {
            let item = fileVersions.find(bi => bi.id === rowId);
            return item;
        }
        return undefined;
    }, [fileVersions]);    
    
    const isActiveCheckboxHandler = useCallback((event: React.ChangeEvent<HTMLInputElement>, rowId: string) => () => {
        const isChecked = event.target.checked;
        if (isChecked) {
            let item = getSelectedRowItem(rowId);
            if (item) {
                // update current row version to be the active version
                setVersionActive(item.id as string);
            }
        }
    }, [getSelectedRowItem, setVersionActive]);

    const downloadHandler = useCallback((blobFileName: string, blobDocId: string) => {
        if (blobDocId?.length && blobFileName?.length) {
            downloadBlob(blobDocId, blobFileName, blobDocumentType);
        }
    }, [downloadBlob, blobDocumentType]);
    
    const getFileVersionRows = () => {
        if (fileVersions && fileVersions.length > 0) {
            return fileVersions.map((fileVersion: BlobStorageModel) => {
                const { id, version, isActive, comment, lastModifiedTime, lastModifiedByName, blobFileName, blobStorageReference } = fileVersion;
                return {
                    _raw: fileVersion,
                    id,
                    version, 
                    isActive,
                    comment, 
                    lastModifiedTime, 
                    lastModifiedByName,
                    blobFileName,
                    blobDocumentId: blobStorageReference?.blobDocumentId,
                } as GridRowModel;
            }) as GridRowModel[];
        } else {
            return [];
        }
    };

    const fileVersionColumns = useMemo<GridColumns<GridRowModel>>(
        () => [
            {
                headerName: 'VERSION',
                field: 'version',
                flex: 1,
                sortable: true,
                cellClassName: "ediDataGridCellFirstChild",
            },
            {
                headerName: 'COMMENT',
                field: 'comment',
                flex: 3,
                sortable: false,
            },
            {
                headerName: 'LAST MODIFIED',
                field: 'lastModifiedTime',
                flex: 1,
                sortable: true,
                type: 'dateTime',
                renderCell: (params: GridRenderCellParams) => {
                    const { value } = params;
                    let dateValue = value ? getShortDateString(value) : undefined;
                    let timeValue = value ? getTimeString(value, { includeSeconds: true }) : undefined;
                    return (
                        <GridCellDualVert header={dateValue} sub={timeValue} />
                    )
                },
            },
            {
                headerName: 'LAST MODIFIED BY',
                field: 'lastModifiedByName',
                flex: 1,
                sortable: false,
            },
            {
                headerName: 'IS ACTIVE',
                field: 'isActive',
                flex: 1,
                headerAlign: 'center',
                align: 'center',
                sortable: false,
                renderCell: (params: GridRenderCellParams) => {
                    const { value, row } = params;
                    if (value) {
                        return <div ><CheckCircleIcon color="success" /></div>
                    }
                    // if not active, show checkbox to allow user to select it as active
                    // allow updating the active version 
                    return (
                        <Checkbox
                            checked={Boolean(value)}
                            onChange={(event) => isActiveCheckboxHandler(event, row.id)()}
                            disabled={value === true}
                            aria-label={`Is Active checkbox for row ${row.id}`}
                            title='Set as Active Version'
                        />
                    );
                },

            },
            {
                field: 'id',
                headerName: 'FILE',
                minWidth: 80,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params: GridRenderCellParams) => {
                    const { row } = params;
                    const { blobFileName, blobDocumentId } = row;

                    return (
                        <GridActionsCellItem
                            label="Download"
                            color="primary"
                            onClick={() => downloadHandler(blobFileName, blobDocumentId)}
                            icon={<Tooltip title="Download"><DownloadIcon /></Tooltip>}
                        />
                    );
                }
            },
        ],
        [downloadHandler, isActiveCheckboxHandler],
    );

    const loadingOverlay = () => {
        return (
            <GridOverlay>
                <CircularProgress aria-label={'progress spinner'} key={'fileVersions-spinner'} size={42} />
            </GridOverlay>
        );
    };

    const noRowsOverlay = () => {
        return (
            <GridOverlay>
                {error && (
                    <Typography variant="body2">
                        Unable to load data. Please try again later.
                    </Typography>
                )}
                {!error && fileVersions?.length === 0 && (
                    <NoRecordsMessage topMargin={6} message="" />
                )}
            </GridOverlay>
        );
    };

    const onSortModelChange = (model: GridSortModel) => {
        setSortModel(model);
    };

    const onAddFileVersionClick = () => {
        setOpenModify(true);
    };

    const onFileVersionDialogSave = (versionFileDetails: BlobUploadRequestInput) => {
        uploadVersion(versionFileDetails);
    };

    return (
        <TabContainer>
            <TabToolbar justify="flex-end">
                <CreateNewButton
                    text="New Version"
                    onClick={onAddFileVersionClick}
                    data-cy="add-new-version"
                />
            </TabToolbar>
            <TabContent>
                <TabDataGridNoRowHover
                    loading={isLoadingVersions || fileVersions === undefined}
                    headerHeight={38}
                    rowHeight={52}
                    aria-label="File Versions List"
                    hideFooter
                    disableColumnMenu
                    disableColumnFilter
                    disableSelectionOnClick
                    rows={fileVersionRows}
                    columns={fileVersionColumns}
                    sortingOrder={['asc', 'desc']}
                    sortModel={sortModel}
                    onSortModelChange={onSortModelChange}
                    components={{
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        LoadingOverlay: loadingOverlay,
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        NoRowsOverlay: noRowsOverlay,
                    }}
                />
            </TabContent>

            <BlobFileUploadDialog
                isOpen={openModify}
                storageFile={fileVersions ? fileVersions[0] : undefined}
                blobDocumentType={blobDocumentType}
                acceptedFileTypes={acceptedFileTypes}
                fileTypeLabel={fileTypeLabel}
                allowedExtensions={allowedExtensions}
                onClose={onFileVersionDialogClose}
                onSave={onFileVersionDialogSave}
                error={error}
                viewerRole={viewerRole}
            />
            {(isDownloading || isUpdating || isUploading) &&
                <CircularProgress
                    aria-label={'progress spinner'}
                    size={48}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-24px',
                        marginLeft: '-24px',
                        zIndex: 1500
                    }}
                />
            }
        </TabContainer>
    );
}

export default BlobFileVersionList;
